import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { TextArea, PillToggle, Select, Field } from "@/components/Form";
import { FullPageLoader } from "@/components/Loader";
import TopWedge from "@/components/TopWedge";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

const SIDE_EFFECTS = ["None", "Nausea", "Headache", "Fatigue", "Sleep changes", "Appetite changes", "GI upset", "Mood shifts"];
const COMPLIANCE = [
  { value: "every_dose", label: "I took every dose" },
  { value: "missed_few", label: "Missed a few doses" },
  { value: "missed_several", label: "Missed several" },
  { value: "stopped", label: "I stopped" },
];

function Scale({ label, value, onChange, testId }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: C.warmGray }}>{label}</div>
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`${testId}-${n}`}
            onClick={() => onChange(n)}
            style={{
              flex: 1,
              padding: "16px 0",
              background: value === n ? C.ink : C.pure,
              color: value === n ? C.peach : C.ink,
              border: `1.5px solid ${value === n ? C.ink : C.warmGray3}`,
              fontSize: 18,
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {n}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 11, color: C.warmGray, letterSpacing: "0.05em" }}>
        <span>NOT GREAT</span>
        <span>EXCELLENT</span>
      </div>
    </div>
  );
}

export default function CheckIn() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [r, setR] = useState({
    overallFeeling: 0,
    sideEffects: [],
    compliance: "",
    weightKg: "",
    energyLevel: 0,
    sleepQuality: 0,
    additionalNotes: "",
  });

  useEffect(() => {
    apiGet("/consumer/subscriptions").then((subs) => {
      const active = (subs || []).find((s) => s.status === "active" || s.status === "paused");
      setSub(active);
    });
  }, []);

  if (sub === null) return <FullPageLoader />;
  if (!sub) {
    return (
      <div style={{ padding: 64, textAlign: "center" }} data-testid="checkin-no-sub">
        <p>No active subscription. Activate first.</p>
        <Link to="/app/plan" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.18em" }}>← BACK TO PLAN</Link>
      </div>
    );
  }

  const valid = r.overallFeeling > 0 && r.compliance && r.energyLevel > 0 && r.sleepQuality > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await apiPost("/consumer/checkins", {
        subscriptionId: sub.id,
        responses: {
          overallFeeling: r.overallFeeling,
          sideEffects: r.sideEffects,
          compliance: r.compliance,
          weightKg: r.weightKg ? Number(r.weightKg) : undefined,
          energyLevel: r.energyLevel,
          sleepQuality: r.sleepQuality,
          additionalNotes: r.additionalNotes,
        },
      });
      toast("Check-in submitted. Thanks!", "success");
      navigate("/app/plan");
    } catch (err) {
      toast(err.detail || "Failed to submit", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSE = (s) => {
    setR((p) => ({ ...p, sideEffects: p.sideEffects.includes(s) ? p.sideEffects.filter((x) => x !== s) : [...p.sideEffects, s] }));
  };

  return (
    <div data-testid="checkin-page" style={{ background: C.cream, paddingBottom: 80 }}>
      <section style={{ background: C.ink, color: C.cream, padding: "56px 24px 72px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ MONTHLY CHECK-IN</Eyebrow>
          <Headline before="HOW'S IT" italic="going?" color={C.cream} italicColor={C.peach} size={64} className="mt-4" />
        </div>
      </section>

      <form onSubmit={submit} style={{ maxWidth: 720, margin: "0 auto", padding: "52px 24px 0", position: "relative", background: C.cream }}>
        <TopWedge color={C.ink} dir="right" />
        <div style={{ position: "relative", zIndex: 2 }}>
        <Scale label="Overall feeling" value={r.overallFeeling} onChange={(v) => setR((p) => ({ ...p, overallFeeling: v }))} testId="checkin-overall" />

        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>SIDE EFFECTS (TAP ALL THAT APPLY)</div>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
            {SIDE_EFFECTS.map((s) => (
              <PillToggle key={s} label={s} active={r.sideEffects.includes(s)} onClick={() => toggleSE(s)} testId={`checkin-se-${s.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} />
            ))}
          </div>
        </div>

        <Select
          label="Compliance"
          value={r.compliance}
          onChange={(v) => setR((p) => ({ ...p, compliance: v }))}
          options={COMPLIANCE}
          testId="checkin-compliance"
        />

        <Field label="WEIGHT (KG) — optional" type="number" value={r.weightKg} onChange={(v) => setR((p) => ({ ...p, weightKg: v }))} testId="checkin-weight" />

        <Scale label="Energy level" value={r.energyLevel} onChange={(v) => setR((p) => ({ ...p, energyLevel: v }))} testId="checkin-energy" />
        <Scale label="Sleep quality" value={r.sleepQuality} onChange={(v) => setR((p) => ({ ...p, sleepQuality: v }))} testId="checkin-sleep" />

        <TextArea label="Anything else?" value={r.additionalNotes} onChange={(v) => setR((p) => ({ ...p, additionalNotes: v }))} testId="checkin-notes" rows={4} />

        <div style={{ marginTop: 32 }}>
          <Button variant="primary" size="lg" block testId="checkin-submit-btn" type="submit" disabled={!valid || submitting}>
            {submitting ? "SUBMITTING…" : "SUBMIT CHECK-IN →"}
          </Button>
        </div>
        </div>
      </form>
    </div>
  );
}
